/**
 * The check before Accept (#1471). "No disagreements" only when a
 * check actually ran and found nothing.
 */

export const DRAFT_CHECK_PREFIX = '@@draft-check@@';

const GENERIC_SOURCES = new Set([
  'book notes',
  'book note',
  'notes',
  'note',
  'a source',
  'source',
  'earlier accepted scenes',
  'earlier accepted scene',
  'earlier scenes',
  'earlier scene',
  'an earlier scene',
  'accepted scenes',
  'accepted scene',
  'scene',
  'scenes',
]);

export function isGenericCheckSource(source) {
  return GENERIC_SOURCES.has(String(source || '').trim().toLowerCase());
}

function cleanItem(item) {
  if (!item || typeof item !== 'object') return null;
  const source = String(item.source || '').trim();
  const draft = String(item.draft || '').trim();
  const other = String(item.other || '').trim();
  if (!source || !draft || !other) return null;
  const kind = item.source_kind === 'scene' ? 'scene' : 'note';
  return { source, source_kind: kind, draft, other };
}

function checkLines(checks) {
  // Keep objects intact. Stringifying them first turns a saved check
  // into "[object Object]", which then looks like the check never arrived.
  if (Array.isArray(checks)) return checks;
  if (typeof checks === 'string') {
    const trimmed = checks.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((line) => (
            line && typeof line === 'object' ? line : String(line ?? '')
          ));
        }
      } catch {
        // A single prefixed line, parsed below.
      }
    }
    return [trimmed];
  }
  if (checks && typeof checks === 'object') return [checks];
  return [];
}

function payloadFromLine(line) {
  if (line && typeof line === 'object') return line;
  const text = String(line || '');
  const at = text.indexOf(DRAFT_CHECK_PREFIX);
  if (at < 0) return null;
  try {
    return JSON.parse(text.slice(at + DRAFT_CHECK_PREFIX.length));
  } catch {
    return null;
  }
}

export function parseDraftCheck(checks) {
  const lines = checkLines(checks);
  const data = lines.map(payloadFromLine).find((payload) => payload && typeof payload === 'object');
  // No payload means the check is not on the page yet, or the line is not
  // a check. That is not a failed check. "Couldn't run" is only the
  // status the server saves when the check itself failed.
  if (!data || typeof data !== 'object') return { status: 'missing', items: [] };
  const items = (Array.isArray(data.items) ? data.items : [])
    .map(cleanItem)
    .filter(Boolean);
  if (data.status === 'ran') return { status: 'ran', items };
  if (data.status === 'could_not_run') return { status: 'could_not_run', items };
  return { status: 'missing', items };
}

/** True only for a finished check with an empty list. */
export function checkFoundNothing(checks) {
  const parsed = parseDraftCheck(checks);
  return parsed.status === 'ran' && parsed.items.length === 0;
}
